import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function ResetPassword({ portal = false }) {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const loginPath = portal ? "/portal/login" : "/login";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== confirm) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post(portal ? "/portal/reset-password" : "/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated — sign in with your new password");
      nav(loginPath);
    } catch (ex) { setErr(formatApiError(ex)); }
    setBusy(false);
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${portal ? "bg-[#F7F5FC]" : "bg-slate-50"}`}>
      <div className="w-full max-w-sm bg-white rounded-2xl border border-[#3B82F6]/10 shadow-sm p-8" data-testid="reset-password-card">
        <div className="font-display font-semibold text-xl text-[#3B82F6]">Hi-Tech Silico</div>
        <div className={`text-[10px] font-semibold uppercase tracking-[0.3em] mb-6 ${portal ? "text-[#8B5CF6]" : "text-[#FC5F5F]"}`}>
          {portal ? "Client Portal" : "Manufacturing CRM"}
        </div>
        <h1 className="text-xl font-display tracking-tight text-[#3B82F6]">Set a new password</h1>
        <p className="text-xs text-[#3B82F6]/50 mt-1 mb-6">Minimum 6 characters.</p>
        {!token ? (
          <p className="text-xs text-[#FC5F5F]" data-testid="reset-no-token">This reset link is invalid. Please request a new one.</p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#3B82F6]/60">New Password</Label>
              <Input type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)}
                     className="mt-1.5" data-testid="reset-password" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#3B82F6]/60">Confirm Password</Label>
              <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                     className="mt-1.5" data-testid="reset-confirm" />
            </div>
            {err && <p className="text-xs text-[#FC5F5F]" data-testid="reset-error">{err}</p>}
            <Button type="submit" disabled={busy || !pw} className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white h-11" data-testid="reset-submit">
              {busy ? "Updating…" : "Update Password"}
            </Button>
          </form>
        )}
        <Link to={loginPath} className="inline-flex items-center gap-1.5 text-xs text-[#3B82F6]/50 hover:text-[#3B82F6] mt-6 transition-colors duration-200" data-testid="reset-back-link">
          <ArrowLeft size={13} /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
